import { isPlatformBrowser } from '@angular/common';
import {
    ENVIRONMENT_INITIALIZER,
    inject,
    Injectable,
    InjectionToken,
    makeEnvironmentProviders,
    PLATFORM_ID,
} from '@angular/core';
import {
    ActivatedRoute,
    PreloadingStrategy,
    PRIMARY_OUTLET,
    Route,
    Router,
    UrlSegment,
    UrlSegmentGroup,
    UrlTree,
    type Params,
} from '@angular/router';
import { Store, type Action } from '@ngrx/store';
import { EMPTY, filter, firstValueFrom, Observable } from 'rxjs';
import { DealerFeature } from '../+state';
import { Dealer } from '../api/api-types';

const allreadyTriggered = new Set<Action>();

export function createActionTrigger(action: Action) {
    return makeEnvironmentProviders([
        {
            provide: ENVIRONMENT_INITIALIZER,
            multi: true,
            useValue: async () => {
                const store = inject(Store);
                const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

                if (allreadyTriggered.has(action)) return;
                if (isBrowser) {
                    allreadyTriggered.add(action);
                }
                store.dispatch(action);
            },
        },
    ]);
}

export function createActionTriggerDealer(action: Action, cond?: (dealer: Dealer) => boolean) {
    return makeEnvironmentProviders([
        {
            provide: ENVIRONMENT_INITIALIZER,
            multi: true,
            useValue: async () => {
                const store = inject(Store);
                const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

                const dealer = await firstValueFrom(
                    store.select(DealerFeature.state.selectDealer).pipe(filter((d) => !!d)),
                    { defaultValue: undefined },
                );

                if (!dealer || (cond && !cond(dealer))) return;

                if (allreadyTriggered.has(action)) return;
                if (isBrowser) {
                    allreadyTriggered.add(action);
                }
                store.dispatch(action);
            },
        },
    ]);
}

export const withPreloadingToken = new InjectionToken<boolean>('withPreloading');

@Injectable({ providedIn: 'root' })
export class HyundaiPreloadingStrategy implements PreloadingStrategy {
    registry = inject(PreloadingRegistry);
    withPreloadingToken = inject(withPreloadingToken);
    loading = new Set<Route>();

    selectedForPreload(route: Route): boolean {
        return route?.data?.['preload'] ?? true;
    }

    preload(route: Route, load: () => Observable<never>) {
        if (!this.withPreloadingToken) return EMPTY;
        if (this.loading.has(route)) return EMPTY;
        if (!route.path) return EMPTY;
        if (!this.selectedForPreload(route)) return EMPTY;
        if (!this.registry.shouldPreload(route)) return EMPTY;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const conn = typeof navigator === 'undefined' ? undefined : (navigator as any).connection;
        if (conn && ((conn.effectiveType || '').includes('2g') || conn.saveData)) return EMPTY;

        this.loading.add(route);

        return load();
    }
}

const prefetchableRoutes: UrlTree[] = [];

@Injectable({ providedIn: 'root' })
export class PreloadingRegistry {
    router = inject(Router);
    activatedRoute = inject(ActivatedRoute);

    visibleLinks = prefetchableRoutes;

    async addLink(route: UrlTree) {
        this.visibleLinks.push(route);
    }

    shouldPreload(route: Route) {
        const fullPath = findPath(this.router.config, route);

        const tree = this.router.parseUrl(fullPath);
        return this.visibleLinks.some(containsTree.bind(null, tree));
    }
}

/**
 * ab hier ist alles nur geklaut https://github.com/mgechev/ngx-quicklink/blob/master/projects/ngx-quicklink/src/lib/prefetch-registry.service.ts
 */

function containsTree(containee: UrlTree, container: UrlTree): boolean {
    return (
        containsQueryParams(container.queryParams, containee.queryParams) &&
        containsSegmentGroup(container.root, containee.root, containee.root.segments)
    );
}

function containsQueryParams(container: Params, containee: Params): boolean {
    return (
        Object.keys(containee).length <= Object.keys(container).length &&
        Object.keys(containee).every((key) => containee[key] === container[key])
    );
}

function containsSegmentGroup(
    container: UrlSegmentGroup,
    containee: UrlSegmentGroup,
    containeePaths: UrlSegment[],
): boolean {
    if (container.segments.length > containeePaths.length) {
        const current = container.segments.slice(0, containeePaths.length);
        if (!equalPath(current, containeePaths)) return false;
        if (containee.hasChildren()) return false;
        return true;
    } else if (container.segments.length === containeePaths.length) {
        if (!equalPath(container.segments, containeePaths)) return false;
        if (!containee.hasChildren()) return true;

        for (const c in containee.children) {
            if (!container.children[c]) break;
            if (containsSegmentGroup(container.children[c], containee.children[c], containee.children[c].segments))
                return true;
        }
        return false;
    } else {
        const current = containeePaths.slice(0, container.segments.length);
        const next = containeePaths.slice(container.segments.length);
        if (!equalPath(container.segments, current)) return false;
        if (!container.children[PRIMARY_OUTLET]) return false;
        return containsSegmentGroup(container.children[PRIMARY_OUTLET], containee, next);
    }
}

function equalPath(as: UrlSegment[], bs: UrlSegment[]): boolean {
    if (as.length !== bs.length) return false;
    return as.every((a, i) => a.path === bs[i].path || a.path.startsWith(':') || bs[i].path.startsWith(':'));
}

// eslint-disable-next-line max-statements
const findPath = (config: Route[], route: Route): string => {
    config = config.slice();
    const parent = new Map<Route, Route>();
    const visited = new Set<Route>();
    while (config.length > 0) {
        const el = config.shift();
        if (!el) continue;

        visited.add(el);
        if (el === route) break;
        let children = el.children || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const current = (el as any)._loadedRoutes || [];
        for (const route of current) {
            if (route && route.children) {
                children = children.concat(route.children);
            }
        }
        for (const r of children) {
            if (visited.has(r)) break;
            parent.set(r, el);
            config.push(r);
        }
    }
    let path = '';
    let current: Route | undefined = route;

    while (current) {
        path = isPrimaryRoute(current) ? `/${current.path}${path}` : `/(${current.outlet}:${current.path}${path})`;
        current = parent.get(current);
    }
    return path.replace(/\/+/, '/');
};

function isPrimaryRoute(route: Route) {
    return route.outlet === PRIMARY_OUTLET || !route.outlet;
}
